import type Store from '@ember-data/store';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { EmberChangeset } from 'ember-changeset';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validateNumber } from 'ember-changeset-validations/validators';
import type IntlService from 'ember-intl/services/intl';
import and from 'ember-truth-helpers/helpers/and';
import InputWithOperator from 'parog-web/components/bio-exam-results/input-with-operator';
import { getNewExpectedDosages } from 'parog-web/components/bio-exam-results/utils/dosage-expected';
import getReferenceValuesMessage from 'parog-web/components/bio-exam-results/utils/reference-value-message';
import type { ExpectedDosage } from 'parog-web/models/bio-exam-expected-dosage-changed-journal-event';
import type BioExamResult from 'parog-web/models/bio-exam-result';
import { type ManuallyMatchedResult } from 'parog-web/models/bio-exam-result';
import type BioExamObservation from 'parog-web/models/loinc';
import type Patient from 'parog-web/models/patient';
import type SemeiaSessionService from 'parog-web/services/session';
import formatNumberAsString from 'parog-web/utils/format-number-as-string';
import type { S as LabeledInputSig } from 'ui-kit/components/changeset/labeled-input';
import LabeledInput from 'ui-kit/components/changeset/labeled-input';
import Dropdown from 'ui-kit/components/dropdown';
import ValidatedField from 'ui-kit/components/validated-field';

interface S {
  Args: Pick<LabeledInputSig['Args'], 'changeset' | 'isReadOnly'> & {
    patient: Patient;
    bioExamObservation: BioExamObservation;
  };
}

export default class BioExamGenericFieldComponent extends Component<S> {
  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  @tracked cleanedValue;
  @tracked operator;
  @tracked declare referenceValueChangeset: EmberChangeset;
  @tracked shouldDisplayReferenceRange = false;

  dropdownPlaceholder?: string;
  declare qualitativeValueTranslation?: string;
  shouldDisplayOperator;

  get dropdownItems() {
    return this.#qualitativeValues.map(qv => {
      return { key: qv.authorizedValue, value: qv.translationFr };
    });
  }

  get isQualitativeResult() {
    return this.valueType === 'enum';
  }

  get propertyName() {
    return `LOINC-${this.#loinc}`;
  }

  get referenceValues() {
    if (this.#referenceValuesMessage) return this.#referenceValuesMessage;

    return getReferenceValuesMessage({
      min: this.#minReferenceValue,
      max: this.#maxReferenceValue,
      prefix: 'Valeurs de référence : '
    });
  }

  get resultLabel() {
    const bioExamObservation = this.args.bioExamObservation;
    const unit = this.#resultDisplayedUnit;
    const name = bioExamObservation.displayLabel;

    if (bioExamObservation.loinc == '11475-1') {
      return 'Si pertinent, précisez le germe :';
    }
    if (bioExamObservation.loinc == '94505-5') {
      return "Si pertinent, précisez l'index d'anticorps :";
    }

    return unit ? `${name}\u00A0(${unit})`.toString() : name;
  }

  get resultPlaceholder() {
    if (this.#isUsingPreferredUnit) return;

    return this.args.bioExamObservation.placeholder;
  }

  get valueType(): string {
    return this.args.bioExamObservation.valueType;
  }

  get #expectedDosage(): ExpectedDosage | undefined {
    const newExpectedDosages = getNewExpectedDosages(this.args.patient) as
      | ExpectedDosage[]
      | undefined;

    if (newExpectedDosages) {
      return newExpectedDosages[`LOINC-${this.#loinc}`];
    }

    return undefined;
  }

  get #isUsingPreferredUnit() {
    if (this.session.medic?.isSuperUser) return false;

    return !!this.#resultPreferredUnit;
  }

  get #isZeroable() {
    return this.args.bioExamObservation.zeroable;
  }

  get #loinc() {
    return this.args.bioExamObservation.loinc;
  }

  get #maxReferenceValue() {
    return (
      this.#expectedDosage?.max ||
      this.args.bioExamObservation.maxReferenceValue
    );
  }

  get #minReferenceValue() {
    return (
      this.#expectedDosage?.min ||
      this.args.bioExamObservation.minReferenceValue
    );
  }

  get #qualitativeValues() {
    return this.args.bioExamObservation.loincQualitativeValues;
  }

  get #referenceValuesMessage() {
    return this.args.bioExamObservation.referenceValuesMessage;
  }

  get #resultDisplayedUnit() {
    const expectedUnit =
      (this.#isUsingPreferredUnit && this.#resultPreferredUnit) ||
      this.#resultReferenceUnit;

    return expectedUnit ? htmlSafe(expectedUnit) : undefined;
  }

  get #resultPreferredUnit() {
    return this.args.patient.healthCentre.preferredUnitForLoincId(
      this.propertyName
    );
  }

  get #resultReferenceUnit() {
    return this.args.bioExamObservation.unit;
  }

  get #validationObject() {
    const referenceValuesValidations = [
      // The number validation is declared on the global form, to prevent the user to write anything that's not a number
      // We put it here also to avoid the validations below to be displayed at the same time because we don't to display
      // Both validations message (validations are tested in the declaration order, and only one is message displayed).
      validateNumber({ string: true, allowBlank: true, message: ' ' })
    ];

    if (!this.#isZeroable) {
      referenceValuesValidations.push(
        validateNumber({
          gt: 0,
          allowBlank: true,
          message: 'Cette valeur doit être supérieure à 0'
        })
      );
    }

    const validationObject = {};
    validationObject[this.propertyName] = referenceValuesValidations;
    return validationObject;
  }

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);

    // The auto-validation is disabled, and we call validate() only when losing the focus on the field
    // The @action onFocusOut
    this.referenceValueChangeset = Changeset(
      {},
      lookupValidator(this.#validationObject),
      this.#validationObject,
      {
        skipValidate: true
      }
    );

    const result = (
      this.args.changeset.get('bioExamResults') as BioExamResult[]
    ).find(res => res.code == this.propertyName);
    this.dropdownPlaceholder = result?.value;
    this.shouldDisplayOperator = result && result.operator != 'equal-to';
    this.operator = result?.operator;
    this.cleanedValue = result?.value;
  }

  onChangesetUpdate = async value => {
    this.#setCleanedValue(value);
    this.#setQualitativeValueTranslation();

    const code = this.propertyName;
    if (!code.startsWith('LOINC')) return;

    await this.#checkReferenceValues();
    const results = this.args.changeset.get(
      'bioExamResults'
    ) as BioExamResult[];
    const existingResult = results?.find(res => res.code == code);

    if (!existingResult) {
      this.args.changeset.set('bioExamResults', [
        ...results,
        this.#newManualBioExamResult()
      ]);
      return;
    }

    this.#updateExistingResult(existingResult);
  };

  onFocusOut = () => {
    if (!this.args.patient) return;
    if (this.#isUsingPreferredUnit) return;
    if (!this.#minReferenceValue && !this.#maxReferenceValue) return;

    if (
      (this.#minReferenceValue as number) > parseFloat(this.cleanedValue) ||
      (this.#maxReferenceValue as number) < parseFloat(this.cleanedValue)
    ) {
      this.shouldDisplayReferenceRange = true;
      return;
    }

    this.shouldDisplayReferenceRange = false;
  };

  updateOperator = async operator => {
    const bioExamResults = this.args.changeset.get(
      'bioExamResults'
    ) as BioExamResult[];
    const existingResult = bioExamResults.find(
      res => res.code == this.propertyName
    );
    if (!existingResult) return;
    existingResult.operator = operator;

    this.args.changeset.set('bioExamResults', [...bioExamResults]);
    await this.onChangesetUpdate(this.args.changeset);
  };

  updateQualitativeValue = async (value: string | string[]): Promise<void> => {
    if (Array.isArray(value)) {
      throw new Error(
        'updateQualitativeValue: multiple selection is not supported here'
      );
    }
    this.args.changeset.set(this.propertyName, value);

    await this.onChangesetUpdate(this.args.changeset);
  };

  updateValue = async event => {
    const target = event.target as HTMLInputElement;

    this.args.changeset.set(this.propertyName, target.value);
    await this.onChangesetUpdate(this.args.changeset);
  };

  async #checkReferenceValues() {
    this.referenceValueChangeset.set(this.propertyName, this.cleanedValue);
    await this.referenceValueChangeset.validate();
  }

  #destroyResult(existingResult: BioExamResult) {
    if (existingResult.id) {
      return this.#flagExistingResultAsToBeDestroyed(existingResult);
    }
    this.#removeUnpersistedResultFromChangeset(existingResult);
  }

  #flagExistingResultAsToBeDestroyed(existingResult: BioExamResult) {
    existingResult._destroy = true;
  }

  #newManualBioExamResult() {
    const unit = this.#isUsingPreferredUnit
      ? this.#resultPreferredUnit
      : this.#resultReferenceUnit;

    const bioExamResult = this.store.createRecord('bio-exam-result', {
      code: this.propertyName,
      value: this.cleanedValue,
      unit,
      selectedRawUnit: unit,
      qualitativeValueTranslation: this.qualitativeValueTranslation,
      operator: 'equal-to',
      label: this.args.bioExamObservation.displayLabel
    });

    bioExamResult.transformAsManualRes();
    return bioExamResult;
  }

  #removeUnpersistedResultFromChangeset(existingResult: BioExamResult) {
    const bioExamResults = (
      this.args.changeset.get('bioExamResults') as BioExamResult[]
    ).filter(bioExamResult => bioExamResult != existingResult);
    this.args.changeset.set('bioExamResults', bioExamResults);
  }

  #setCleanedValue(value) {
    this.cleanedValue =
      typeof value === 'string'
        ? value
        : formatNumberAsString(value.get(this.propertyName));
  }

  #setQualitativeValueTranslation() {
    this.qualitativeValueTranslation = this.dropdownItems?.find(
      item => item.key === this.cleanedValue
    )?.value;
  }

  #updateExistingResult(existingResult: BioExamResult) {
    if (existingResult.isManualMatching()) {
      this.#updateRawValue(existingResult);
    }

    if (existingResult.value == this.cleanedValue) return;

    existingResult.value = this.cleanedValue;

    if (isEmpty(this.cleanedValue)) {
      this.#destroyResult(existingResult);
      return;
    }

    existingResult.qualitativeValueTranslation =
      this.qualitativeValueTranslation;
  }

  #updateRawValue(existingResult: ManuallyMatchedResult) {
    const segs = existingResult.originalLine.raw.segs;
    segs[4] = this.cleanedValue;
    segs[5] = this.#resultDisplayedUnit?.toString() ?? '';
  }

  <template>
    <ValidatedField
      @propertyName={{this.propertyName}}
      @changeset={{this.referenceValueChangeset}}
      @errorClass='right-aligned-validation-error-message'
      data-test-validation-message={{this.propertyName}}
    >
      {{#if this.shouldDisplayOperator}}
        <label>
          {{this.resultLabel}}
        </label>
        <InputWithOperator
          @onOperatorUpdated={{this.updateOperator}}
          @onValueUpdated={{this.updateValue}}
          @currentOperator={{this.operator}}
          @inputValue={{this.cleanedValue}}
          @propertyName={{this.propertyName}}
        />
      {{else if this.isQualitativeResult}}
        <Dropdown
          @labelName={{this.resultLabel}}
          @propertyName={{this.propertyName}}
          @onChange={{this.updateQualitativeValue}}
          @selected={{this.cleanedValue}}
          @placeholder={{this.dropdownPlaceholder}}
          as |dropdown|
        >
          {{#each this.dropdownItems as |item|}}
            <dropdown.Item @key={{item.key}} @value={{item.value}} />
          {{/each}}
        </Dropdown>
      {{else}}
        <LabeledInput
          @changeset={{@changeset}}
          @onChangesetUpdate={{this.onChangesetUpdate}}
          @labelName={{this.resultLabel}}
          @value={{this.cleanedValue}}
          @type={{this.valueType}}
          @placeholder={{this.resultPlaceholder}}
          @propertyName={{this.propertyName}}
          @onFocusOut={{this.onFocusOut}}
          @isReadOnly={{@isReadOnly}}
          @errorClass='right-aligned-validation-error-message'
        />
        {{#if
          (and
            this.shouldDisplayReferenceRange
            this.referenceValueChangeset.isValid
          )
        }}
          <p class='text-green absolute right-0 max-[768px]:relative'>
            {{this.referenceValues}}
          </p>
        {{/if}}
      {{/if}}
    </ValidatedField>
  </template>
}
